import React, { lazy, ReactElement, useContext, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { useRouter } from 'next/router';
import find from 'lodash/find';
import { toggleModal, toggleShowMobileMenu } from '@Hotels-at-Home/hah-fe-core/dist/store/modal';
import { selectShowModal } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/modal';
import {
  useMarriottAdobeLaunch,
  useAnalyticsScripts,
  useGetPageClass,
  useConsentModeListener,
  useInterval,
  useFirstVisit,
  useWindowSize,
  useScrollRestoration,
  useCatalogId,
  useRumScript,
} from '@Hotels-at-Home/hah-fe-core/dist/hooks';
import {
  SM_WIDTH,
  COOKIE_CHECKOUT_START,
  COOKIE_CURRENCY_NAME,
  COOKIE_LANGUAGE_NAME,
  LanguageCode,
} from '@Hotels-at-Home/hah-fe-core/dist/constants';
import SiteLinks from '@Hotels-at-Home/hah-fe-core/dist/components/Header/SiteLinks';
import Header from '@Hotels-at-Home/hah-fe-core/dist/components/Header';
import SearchDrawer from '@Hotels-at-Home/hah-fe-core/dist/components/SearchDrawer';
import ShoppingCartDrawer from '@Hotels-at-Home/hah-fe-core/dist/components/CartDrawer';
import BannerWrapper from '@Hotels-at-Home/hah-fe-core/dist/components/BannerWrapper';
import {
  ActionType,
  IFooter,
  INavMenu,
  ModalType,
  IGetPromotionResponse,
  UtilityIcons,
} from '@Hotels-at-Home/hah-fe-core/dist/types';

import { useDispatch, useSelector } from '~/store/hooks';
import {
  getCookie,
  getCookies,
  IConsentMode,
  removeCookie,
  setFirstVisitCookie,
  setPreferredLangCookie,
} from '@Hotels-at-Home/hah-fe-core/dist/utils/cookies';
import Footer from '../components/layouts/Footer';
import {
  AuthSidebar,
  CurrencyDrawer,
  LanguageDrawer,
} from '@Hotels-at-Home/hah-fe-core/dist/components';

import {
  cloudWatchRum,
  cookieRestorerEnabled,
  cookieRestorerOptions,
} from '~/constants/siteConfig';
import { siteLinks, bonvoyLogo } from '~/constants/siteLinks';

import classnames from 'classnames';

import FontFaceObserver from 'fontfaceobserver';
import { getHrefLinks } from '~/utils/hreflangs';
import { selectProduct } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/product';
import { selectCategory } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/category';
import { getTermsAndConditionsByCurrency } from '@Hotels-at-Home/hah-fe-core/dist/utils/promotion';

import Modal from '@Hotels-at-Home/hah-fe-core/dist/components/Modal';
import BmsmChart from '../components/common/Banner/BmsmChart';
import { CookieRestorer } from '@Hotels-at-Home/hah-fe-core/dist/utils/cookieRestorer';
import { BonvoyAuthContext } from '@Hotels-at-Home/hah-fe-core/dist/context/auth';

const FirstVisitModal = lazy(
  () => import('@Hotels-at-Home/hah-fe-core/dist/components/FirstVisitModal'),
);

interface IProps {
  userAgent: string;
  children: React.ReactNode;
  navMenu: INavMenu;
  footer: IFooter;
  promotion: IGetPromotionResponse;
  networkErrorStatusCode: number;
  isSafari: boolean;
}

const AppLayout = ({
  userAgent,
  children,
  navMenu,
  footer,
  promotion,
  networkErrorStatusCode,
  isSafari,
}: IProps): ReactElement => {
  useCatalogId();
  useScrollRestoration();
  const { pathname, route } = useRouter();
  const [bodyFontLoaded, setBodyFontLoaded] = useState(false);
  const [headingFontLoaded, setHeadingFontLoaded] = useState(false);
  const [consentMode, setConsentMode] = useState<IConsentMode | null>(null);
  const [shouldIntervalBeCancelled, setShouldIntervalBeCancelled] = useState(false);

  const windowSize = useWindowSize();
  const isSmallScreen = Boolean(windowSize.width) && windowSize.width <= SM_WIDTH;
  const { isBonvoyLoggedIn } = useContext(BonvoyAuthContext);

  useConsentModeListener((consent: any) => {
    setConsentMode(consent);
  });

  const currency = getCookie(COOKIE_CURRENCY_NAME) as string;
  const lang = getCookie(COOKIE_LANGUAGE_NAME) as LanguageCode;

  useAnalyticsScripts();
  useRumScript(cloudWatchRum);
  useMarriottAdobeLaunch(process.env.NEXT_PUBLIC_ADOBE_LAUNCH_URL);

  const dispatch = useDispatch();
  const showModal = useSelector(selectShowModal);
  const product = useSelector(selectProduct);
  const { category } = useSelector(selectCategory);

  useEffect(() => {
    gsap.config({ nullTargetWarn: false });
  }, []);

  const cookies = getCookies();

  const pageClass = useGetPageClass();

  const termsAndConditionsByCurrency: string = getTermsAndConditionsByCurrency(
    promotion?.promotion_terms,
    currency,
  );

  const handleToggleModal = (modal: ModalType) => {
    dispatch(toggleModal(modal));
  };

  const onAction = (action: ActionType) => {
    let modalType;

    switch (action) {
      case ActionType.Account: {
        modalType = ModalType.Account;
        break;
      }
      case ActionType.Search: {
        modalType = ModalType.Search;
        break;
      }
      case ActionType.Language: {
        modalType = ModalType.Language;
        break;
      }
      case ActionType.Currency: {
        modalType = ModalType.Currency;
        break;
      }
      case ActionType.ShoppingCart: {
        modalType = ModalType.ShoppingCart;
        break;
      }
      default: {
        break;
      }
    }

    handleToggleModal(modalType);
  };

  const closeHandler = () => {
    if (showModal) {
      const idString = `${showModal.toLocaleLowerCase()}-button`;
      const actionButton = document.getElementById(idString);
      if (actionButton) {
        actionButton.focus();
      }
    }
    dispatch(toggleModal(null));
    dispatch(toggleShowMobileMenu(null));
  };

  const removeCheckoutCookies = () => {
    if (!pathname.includes('checkout') && cookies[COOKIE_CHECKOUT_START]) {
      removeCookie(COOKIE_CHECKOUT_START);
    }
  };

  const { isFirstVisitModalOpened, setIsFirstVisitModalOpened, handleClose } = useFirstVisit();

  useEffect(() => {
    if (!cookieRestorerEnabled || !isSafari) return;

    CookieRestorer(cookieRestorerOptions);

    const interval = setInterval(() => {
      CookieRestorer(cookieRestorerOptions);
    }, cookieRestorerOptions.options.interval || 5000);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    removeCheckoutCookies();
  }, [pathname]);

  const isHomepage = !route.includes('/p/') && !route.includes('/c/');

  const isMiscPages =
    route.includes('/404') ||
    route.includes('/search') ||
    route.includes('/cart') ||
    route.includes('/help');

  const hrefLinks = getHrefLinks(isHomepage, product, category);

  const checkWindowConsentMode = () => {
    const consentCookie = getCookie('CookieConsent') as IConsentMode;

    if (consentCookie) {
      const consent: IConsentMode = consentCookie;

      if (consent) {
        setConsentMode(consent);

        setShouldIntervalBeCancelled(true);
      }
    }

    if (
      window?.CookieConsent?.consent !== undefined &&
      Object.values(window.CookieConsent.consent).every(e => e)
    ) {
      setFirstVisitCookie(userAgent, window.CookieConsent.consent);

      setConsentMode(window.CookieConsent.consent);

      setShouldIntervalBeCancelled(true);
    }
    if (
      window?.ConsentMode?.consent !== undefined &&
      Object.values(window.ConsentMode.consent).every(e => e)
    ) {
      setConsentMode(window.ConsentMode.consent);
      setShouldIntervalBeCancelled(true);
    }
    if (
      window?.Cookiebot?.consent !== undefined &&
      Object.values(window.Cookiebot.consent).every(e => e)
    ) {
      setConsentMode(window.Cookiebot.consent);
      setShouldIntervalBeCancelled(true);
    }
  };

  useInterval(
    () => {
      checkWindowConsentMode();
    },
    shouldIntervalBeCancelled ? null : 1000,
  );

  useEffect(() => {
    if (consentMode) {
      setPreferredLangCookie(userAgent, currency, lang);
    }
  }, [consentMode]);

  const [showBMSMModal, setShowBMSMModal] = useState(false);

  const { promo_end: promoEnd } = promotion;

  const endDate = new Date(promoEnd);
  const endDay = endDate.toLocaleDateString('en-US', { weekday: 'long' });

  const openPopup = () => {
    setShowBMSMModal(true);
  };

  const closePopup = () => {
    setShowBMSMModal(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      openPopup();
    }
  };

  let RenderBanners;

  const [shouldHideBanner, setShouldHideBanner] = useState(false);

  // Extract common promotion conditions to improve readability
  const showStandardPromo = promotion?.promo_is_active && promotion?.promo_type !== 'bmsm';
  const showBmsmPromo = promotion?.promo_is_active && promotion?.promo_type === 'bmsm';
  const showOnNonHomepage = !isHomepage || isMiscPages;

  const countdownIsActive = promotion.promo_days_left <= 3;

  if (shouldHideBanner) {
    RenderBanners = null;
  } else if (showStandardPromo && showOnNonHomepage) {
    RenderBanners = <BannerWrapper promotion={promotion} />;
  } else if (showBmsmPromo && showOnNonHomepage) {
    RenderBanners = (
      <>
        <div
          role="button"
          tabIndex={0}
          aria-pressed="false"
          className="bmsm-banner-wrapper"
          onClick={openPopup}
          onKeyDown={handleKeyDown}
        >
          <BannerWrapper promotion={promotion} />
        </div>
        <Modal
          open={showBMSMModal}
          maxWidth="sm"
          className="base-banner-lightbox bmsm-lightbox"
          onClose={closePopup}
        >
          <div className="content">
            <BmsmChart
              promotion={promotion}
              daysLeft={countdownIsActive ? promotion.promo_days_left : null}
              ends={endDay}
              currency={currency}
            />
          </div>
        </Modal>
      </>
    );
  } else {
    RenderBanners = null;
  }

  return (
    <div
      id="app"
      className={classnames(
        {
          safari: isSafari,
          'body-font-loaded': true,
          'heading-font-loaded': true,
        },
        pageClass,
      )}
    >
      <SiteLinks items={siteLinks} logo={bonvoyLogo} />
      <Header
        navMenu={navMenu}
        showActions={!networkErrorStatusCode}
        onAction={onAction}
        transparent
        breakNav="always"
        blockNavChildren
        sticky
        iconOrder={[
          UtilityIcons.ShoppingCart,
          UtilityIcons.Language,
          UtilityIcons.Search,
          UtilityIcons.Currency,
          UtilityIcons.Account,
        ]}
      />
      {RenderBanners}
      <main id="content" className="content">
        {children}
      </main>
      <Footer
        footer={footer}
        termsAndConditions={termsAndConditionsByCurrency}
        promotion={promotion}
      />
      <SearchDrawer isOpen={showModal === ModalType.Search} onClose={closeHandler} />
      <CurrencyDrawer isOpen={showModal === ModalType.Currency} onClose={closeHandler} />
      <LanguageDrawer
        enableNA
        enableAPAC
        hrefLinks={hrefLinks}
        isOpen={showModal === ModalType.Language}
        onClose={closeHandler}
      />
      <AuthSidebar
        isLogged={isBonvoyLoggedIn}
        isOpen={showModal === ModalType.Account}
        onClose={closeHandler}
      />
      <ShoppingCartDrawer isOpen={showModal === ModalType.ShoppingCart} onClose={closeHandler} />
      {isFirstVisitModalOpened && !isSmallScreen && (
        <FirstVisitModal
          onClose={handleClose}
          privacyPolicy={find(footer?.bottomFooterLinks, { name: 'privacy-policy' })}
        />
      )}
    </div>
  );
};

export default AppLayout;
